import React, { useState, useEffect, useRef, useCallback } from 'react'
import Axios from 'axios'
import Print from '../../../Common_Files/Print';
import { Host } from '../../../App';

function SlapFoam_Inventory({ partDataset, onhandparts, AllColumns, Area }) {
  const token = (window.localStorage.getItem("acessToken"));
  const userName = (window.localStorage.getItem("Name") || '').replace(/[{""}]/g, "");
  const currentDate = new Date();
  const [Inventory_Date, setInventory_Date] = useState(currentDate.toISOString().slice(0, 10));

  const [filterText, setFilterText] = useState({ customer: '', desc: '' });
  const [allParts, setAllParts] = useState([]);
  const [filteredParts, setfilteredParts] = useState([]);

  const [printTrigger, setPrintTrigger] = useState(false);
  const tableRef = useRef(null);

  const handlePrint = () => {
    const tableHTML = tableRef.current?.innerHTML;
    setPrintTrigger({ html: tableHTML });
  };

  const [del, setdel] = useState(0);

  // ---------- Helpers ----------
  const norm = (txt) =>
    String(txt || '')
      .replace(/[–—]/g, '-') // normalize dashes
      .replace(/^\s*(Main\s*Part\s*-\s*|Sub\s*Part\s*-\s*)/i, '') // drop prefixes
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();

  // rank within a customer: BODY -> 0, SEAT -> 1, other -> 2
  const sectionRank = (internalNo) => {
    const t = norm(internalNo);
    if (/\bBODY\b/.test(t)) return 0;
    if (/\bSEAT\b/.test(t)) return 1;
    return 2;
  };

  const rowSorter = (a, b) => {
    const ca = String(a.Customer || '');
    const cb = String(b.Customer || '');
    const byCustomer = ca.localeCompare(cb);
    if (byCustomer !== 0) return byCustomer;

    const ra = sectionRank(a.Part_internalNo);
    const rb = sectionRank(b.Part_internalNo);
    if (ra !== rb) return ra - rb;

    return String(a.Part_internalNo || '').localeCompare(String(b.Part_internalNo || ''));
  };

  // Build on-hand map: { "LILLY BODY -S": 64, ... }
  const [onHandMap, setOnHandMap] = useState({});
  useEffect(() => {
    const map = {};
    (onhandparts || []).forEach(row => {
      const s = String(row?.Pending_Parts_List || '');
      if (!s) return;

      s.split(',').forEach(chunk => {
        const c = chunk.trim();
        // capture "(<name> -S) (<qty>)", ignoring prefixes
        const m = c.match(/(?:Main\s*Part\s*-\s*|Sub\s*Part\s*-\s*)?(.*?-S)\s*\((\-?\d+)\)/i);
        if (m) {
          const internal = norm(m[1]);
          const qty = Number(m[2]) || 0;
          map[internal] = qty; // last value wins
        }
      });
    });
    setOnHandMap(map);
  }, [onhandparts]);

  const getOnHandR = useCallback(
    (internalNo) => onHandMap[norm(internalNo)] ?? '',
    [onHandMap]
  );

  // ---------- Seed page rows (only -S parts) ----------
  useEffect(() => {
    const initializeParts = () => {
      const savedData = localStorage.getItem('SlapFoamunsavedPartsData'); // keeping key as-is
      const base = (partDataset || [])
        .filter(part => String(part.Part_internalNo || '').trim().toUpperCase().endsWith('-S'))
        .map(part => ({ ...part }));

      let parts;
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          parts = base.map(part => {
            const savedMatch = parsed.find(p => p.Part_internalNo === part.Part_internalNo);
            return savedMatch
              ? { ...part, ...savedMatch }
              : { ...part, QTY: part.QTY ?? '', User: userName };
          });
        } catch {
          localStorage.removeItem('SlapFoamunsavedPartsData');
          parts = base.map(part => ({ ...part, QTY: part.QTY ?? '', User: userName }));
        }
      } else {
        parts = base.map(part => ({ ...part, QTY: part.QTY ?? '', User: userName }));
      }

      parts.sort(rowSorter);
      setAllParts(parts);
      setfilteredParts(parts);
    };

    if (Array.isArray(partDataset) && partDataset.length > 0) initializeParts();
  }, [partDataset, userName]);

  // ---------- Filtering + keep desired sort ----------
  useEffect(() => {
    const filtered = allParts.filter(part =>
      (filterText.customer === '' || String(part.Customer || '').toLowerCase().includes(filterText.customer.toLowerCase())) &&
      (filterText.desc === '' || String(part.Part_internalNo || '').toLowerCase().includes(filterText.desc.toLowerCase()))
    );
    filtered.sort(rowSorter);
    setfilteredParts(filtered);
  }, [filterText, allParts]);

  // Persist edits locally
  useEffect(() => {
    localStorage.setItem('SlapFoamunsavedPartsData', JSON.stringify(allParts));
  }, [allParts]);

  const updateDatas = (partObj, key, value) => {
    setAllParts(prev => {
      const updatedParts = prev.map(row =>
        row.Part_internalNo === partObj.Part_internalNo ? { ...row, [key]: value } : row
      );
      updatedParts.sort(rowSorter);
      return updatedParts;
    });
  };

  const handleInputChange = (e, index, key) => {
    const { value } = e.target;
    const row = allParts[index];
    if (!row) return;
    updateDatas(row, key, value);
  };

  // ✅ QTY Value button: fill QTY from On hand parts (ONLY if QTY is empty)
  const FillQtyFromOnHand = () => {
    if (!Object.keys(onHandMap).length) {
      window.alert("No on-hand data found to fill.");
      return;
    }

    setAllParts(prev => {
      let changed = false;

      const updated = prev.map(p => {
        const current = p.QTY;
        const onH = getOnHandR(p.Part_internalNo);

        const isEmpty =
          current === '' || current === null || typeof current === 'undefined';

        if (isEmpty && onH !== '') {
          changed = true;
          return { ...p, QTY: String(onH) };
        }
        return p;
      });

      if (!changed) {
        window.alert("Nothing to fill (QTY already entered for these rows).");
        return prev;
      }

      const sorted = [...updated].sort(rowSorter);
      return sorted;
    });
  };

  const Update = async () => {
    const changedData_ = allParts
      .filter(part => Number(part.QTY) > 0)
      .map(part => ({
        ...part,
        Date: Inventory_Date,
        Area: "Slap Foam" // keeping same as your API expects
      }));

    if (!changedData_.length) {
      window.alert("No QTY entered (must be > 0) — nothing to update.");
      return;
    }

    try {
      const res = await Axios.post(`https://` + Host + `/Part_Inventory/SlapFoam/Add`, {
        changedData: JSON.stringify(changedData_),
        token
      });

      if (res.status === 200) {
        alert(res.data.message);
        localStorage.removeItem('SlapFoamunsavedPartsData');
        window.location.reload();
        setdel(d => d + 1);
      }
    } catch (err) {
      console.error(err);
      window.alert("Update failed. Check console/network for error.");
    }
  };

  const ClearAll = () => {
    localStorage.removeItem('SlapFoamunsavedPartsData');
    window.alert("Cleared !");
    window.location.reload();
  };

  // how many columns for the customer header row
  const colCount = AllColumns ? 8 : 6;

  return (
    <div>
      <div className="row g-2 mb-2">
        <div className="col">
          <input
            type="text"
            className="form-control"
            placeholder="Filter by Customer"
            onChange={(e) => setFilterText({ ...filterText, customer: e.target.value })}
            list='Customer'
          />
          <datalist id="Customer">
            {[...new Set(allParts.map(x => x.Customer).filter(Boolean))].map((Customer, index) => (
              <option key={index} value={Customer} />
            ))}
          </datalist>
        </div>

        <div className="col">
          <input
            type="text"
            className="form-control"
            placeholder="Filter by Internal Part"
            list="PartDescription"
            onChange={(e) => setFilterText({ ...filterText, desc: e.target.value })}
          />
          <datalist id="PartDescription">
            <option></option>
            {allParts.map(x => <option key={x.Part_internalNo}>{x.Part_internalNo}</option>)}
          </datalist>
        </div>

        <div className="col-auto d-flex align-items-center">
          <input
            type="date"
            className="form-control"
            value={Inventory_Date}
            onChange={(e) => setInventory_Date(e.target.value)}
            style={{ maxWidth: 180 }}
          />
        </div>

        <div className="col-auto d-flex align-items-center gap-2">
          <button className="btn btn-success" onClick={FillQtyFromOnHand}>QTY Value</button>
          <button className='btn btn-info' onClick={handlePrint}>Print</button>
          <button className='btn btn-primary' onClick={Update}>Update</button>
          <button className='btn btn-secondary' onClick={ClearAll}>Clear All</button>
        </div>
      </div>
      <div className="inventory_table">
        <table className="table table-sm table-striped" id="alldata_openorder" ref={tableRef}>
          <thead>
            <tr>
              <th>PART NO</th>
              <th>INTERNAL PART NUMBER</th>
              {AllColumns ? <th>PART DESCRIPTION</th> : null}
              <th>CUSTOMER</th>
              {AllColumns ? <th>CATEGORY</th> : null}
              {AllColumns ? <th>TYPE</th> : null}
              <th>QTY</th>
              <th>On hand parts</th>
            </tr>
          </thead>

          <tbody>
            {filteredParts.length ? (
              filteredParts.map((val, i) => {
                const realIndex = allParts.findIndex(p => p.Part_internalNo === val.Part_internalNo);
                const showCustomerHeader = i === 0 || String(filteredParts[i - 1].Customer || '') !== String(val.Customer || '');
                const onHandDisplay = getOnHandR(val.Part_internalNo);

                return (
                  <React.Fragment key={val.Part_internalNo}>
                    {showCustomerHeader && (
                      <tr className="table-secondary">
                        <td colSpan={colCount}><strong>Customer: {val.Customer || '—'}</strong></td>
                      </tr>
                    )}

                    <tr>
                      <td>{val.Part_No || val.PartNo}</td>
                      <td>{val.Part_internalNo}</td>
                      {AllColumns ? <td>{val.Part_Description || val.Part_description}</td> : null}
                      <td>{val.Customer}</td>
                      {AllColumns ? <td>{val.CATEGORY}</td> : null}
                      {AllColumns ? <td>{val.Type}</td> : null}

                      {/* ✅ Always editable + always stored in state */}
                      <td style={{ minWidth: 90 }}>
                        <input
                          type="number"
                          className="form-control form-rounded"
                          value={val.QTY ?? ''}
                          placeholder={onHandDisplay !== '' ? String(onHandDisplay) : ''}
                          onChange={(e) => handleInputChange(e, realIndex, 'QTY')}
                        />
                      </td>

                      <td>{onHandDisplay}</td>
                    </tr>
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={colCount} className="text-center text-muted">
                  No records to display
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {printTrigger && (
        <Print
          tableHTML={printTrigger.html}
          user="Vikas"
          title={Area}
          onComplete={() => setPrintTrigger(false)}
        />
      )}
    </div>
  );
}

export default SlapFoam_Inventory;
